import {
  ArrowsRightLeftIcon,
  ArrowTopRightOnSquareIcon,
  SparklesIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import SharedButton from '../../../../global/Button/Button';
import SharedBadge from '@/global/Badge/Badge';
import { BottomDetailValueWithTooltip } from './BottomDetailValueWithTooltip';
import {
  DetailGroup,
  getRowDetailContextLabel,
  normalizeDetailValue,
  RIGHT_SIDEBAR_COLLAPSED_WIDTH_PX,
} from '../utils/searchResultsHelpers';

interface RelationSummary {
  leftEntity: string;
  rightEntity: string;
  showRelationArrow: boolean;
  diseaseType: string;
  score: string;
  pmids: string;
  chem: string;
  variant: string;
}

interface BottomDetailPanelProps {
  bottomDetailRow: any;
  bottomDetailHeight: number;
  groupedBottomDetails: DetailGroup[];
  relationSummary: RelationSummary;
  source: any;
  tab: any;
  isSidebarOpen: boolean;
  sidebarWidth: number | string;
  isDesktop: boolean;
  searchParams: any;
  setSearchParams: (params: any) => void;
  onClose: () => void;
  startBottomResize: (startY: number) => void;
}

export function BottomDetailPanel({
  bottomDetailRow,
  bottomDetailHeight,
  groupedBottomDetails,
  relationSummary,
  source,
  tab,
  isSidebarOpen,
  sidebarWidth,
  isDesktop,
  searchParams,
  setSearchParams,
  onClose,
  startBottomResize,
}: BottomDetailPanelProps) {
  if (!bottomDetailRow) return null;

  const rightOffset = isDesktop ? (isSidebarOpen ? sidebarWidth : RIGHT_SIDEBAR_COLLAPSED_WIDTH_PX) : 0;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-30 flex flex-col overflow-hidden border-t border-slate-200 bg-white shadow-[0_-8px_24px_rgba(0,0,0,0.08)]"
      style={{ height: bottomDetailHeight, right: rightOffset, transition: 'right 0.3s ease-out' }}
    >
      <div className="shrink-0 border-b border-slate-200 px-4 py-2 bg-gradient-to-r from-pink-50 to-white">
        <div
          className="mx-auto flex w-fit cursor-grab items-center gap-2 px-3 py-1 text-xs font-medium text-slate-600 text-center transition-colors hover:bg-slate-100 active:bg-slate-200 justify-center"
          onMouseDown={event => {
            event.preventDefault();
            startBottomResize(event.clientY);
          }}
          onTouchStart={event => {
            if (!event.touches.length) return;
            startBottomResize(event.touches[0].clientY);
          }}
          role="separator"
          aria-orientation="horizontal"
          aria-label="Drag to resize detail panel"
          title="Drag to resize"
        >
          <span className="drag-handle" style={{ rotate: '90deg' }}>
            ⋮⋮
          </span>
        </div>
        <div className="flex items-center justify-between mt-3">
          <div className="flex items-center gap-2">
            <span className="bg-pink p-2 rounded-xl mr-2">
              <SparklesIcon className="w-5 h-5 text-white" />
            </span>
            <div className="flex flex-col gap-1">
              <span className="text-xs font-normal text-gray-400 tracking-widest text-uppercase">
                {getRowDetailContextLabel(tab as string)}
              </span>
              <div className="flex items-center gap-2">
                <span className="text-lg font-semibold text-uppercase">{relationSummary.leftEntity}</span>
                {relationSummary.showRelationArrow && (
                  <>
                    <ArrowsRightLeftIcon className="w-4 h-4 text-gray-400" />
                    <span className="text-sm font-normal">{relationSummary.rightEntity}</span>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <SharedBadge
              text={`Source: ${source}`}
              icon={<SparklesIcon className="w-4 h-4 mr-1" />}
              variant="primaryLight"
            />
          </div>
          <div className="flex items-center gap-2">
            <SharedButton
              type="button"
              variant="primaryOutline"
              size="xs"
              iconRight={<ArrowTopRightOnSquareIcon className="w-4 h-4 mb-1" />}
              onClick={() => {
                const nextParams = new URLSearchParams(searchParams as any);
                nextParams.set('fullRecord', '1');
                setSearchParams(nextParams);
              }}
            >
              Open full record
            </SharedButton>
            <XMarkIcon className="w-4 h-4 mb-1 cursor-pointer" strokeWidth={2} onClick={onClose} />
          </div>
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        <div className="min-w-0 grid grid-cols-1 gap-3 xl:grid-cols-3">
          {groupedBottomDetails.map(group => (
            <section
              key={`bottom-${group.title}`}
              className="min-w-0 rounded-xl border border-slate-200 pt-0 pb-3"
            >
              <h3 className="mb-2 flex min-h-[2.25rem] items-center justify-between gap-2 text-xs font-semibold uppercase tracking-widest text-slate-600 py-2 px-3 bg-gradient-to-r from-pink-50 to-white rounded-t-xl border-b border-slate-200">
                <span className="min-w-0 truncate">• {group.title}</span>
                <span
                  className="shrink-0 tabular-nums font-light text-gray-500"
                  aria-label={`${group.entries.length} fields in this group`}
                  style={{ fontSize: '0.7rem' }}
                >
                  {group.entries.length}
                </span>
              </h3>
              <div className="space-y-1 px-3">
                {group.entries.map(entry => (
                  <div
                    key={`${group.title}-${entry.key}`}
                    className="flex min-w-0 items-center justify-between gap-2 text-xs leading-[1.1]"
                  >
                    <span
                      className="w-[44%] shrink-0 overflow-hidden text-ellipsis whitespace-nowrap text-slate-500 tracking-wide"
                      title={entry.displayLabel}
                    >
                      {entry.displayLabel}
                    </span>
                    <BottomDetailValueWithTooltip text={normalizeDetailValue(entry.value, entry.key)} />
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
